import React, { useState } from 'react';
import {
  Target, TrendingUp, BookOpen, Award, Clock, Users,
  CheckCircle, Lock, Play, Star, ArrowRight, Briefcase,
  MapPin, Filter, Search, Calendar, DollarSign,
  BarChart3, Trophy, Sparkles, Building, ChevronRight,
  User, Eye, Heart, Share2, BookmarkPlus, ExternalLink,
  Layers, Code, Database, Palette, Brain, Shield,
  MessageSquare, Video, FileText, Download
} from 'lucide-react';

interface CareerPathStep {
  id: string;
  title: string;
  description: string;
  estimatedTime: string;
  status: 'completed' | 'current' | 'locked' | 'available';
  courses: number;
  skills: string[];
  type: 'course' | 'project' | 'assessment' | 'milestone';
}

interface CareerPath {
  id: string;
  title: string;
  description: string;
  category: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  duration: string;
  totalSteps: number;
  completedSteps: number;
  estimatedSalary: {
    min: number;
    max: number;
  };
  jobOpenings: number;
  growthRate: number;
  skills: string[];
  steps: CareerPathStep[];
  prerequisites?: string[];
  outcomes: string[];
  companies: string[];
}

interface JobOpportunity {
  id: string;
  title: string;
  company: string;
  location: string;
  type: 'full-time' | 'part-time' | 'contract' | 'remote';
  salary: {
    min: number;
    max: number;
  };
  requiredSkills: string[];
  matchPercentage: number;
  postedDate: string;
  applicants: number;
}

export default function CareerPath() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'paths' | 'jobs'>('paths');
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  const categories = [
    { id: 'all', name: 'All Paths', icon: <Target className="w-4 h-4" /> },
    { id: 'development', name: 'Development', icon: <Code className="w-4 h-4" /> },
    { id: 'design', name: 'Design', icon: <Palette className="w-4 h-4" /> },
    { id: 'data', name: 'Data Science', icon: <Database className="w-4 h-4" /> },
    { id: 'ai', name: 'AI/ML', icon: <Brain className="w-4 h-4" /> },
    { id: 'security', name: 'Cybersecurity', icon: <Shield className="w-4 h-4" /> },
    { id: 'management', name: 'Management', icon: <Briefcase className="w-4 h-4" /> }
  ];

  const careerPaths: CareerPath[] = [
    {
      id: '1',
      title: 'Full Stack Web Developer',
      description: 'Master both frontend and backend development to build complete web applications',
      category: 'development',
      level: 'intermediate',
      duration: '8-12 months',
      totalSteps: 12,
      completedSteps: 4,
      estimatedSalary: { min: 70000, max: 120000 },
      jobOpenings: 15420,
      growthRate: 13.2,
      skills: ['React', 'Node.js', 'MongoDB', 'JavaScript', 'Express', 'Git'],
      prerequisites: ['Basic HTML/CSS', 'Programming fundamentals'],
      outcomes: [
        'Build full-stack web applications',
        'Work with databases and APIs',
        'Deploy applications to cloud platforms',
        'Lead development projects'
      ],
      companies: ['Google', 'Microsoft', 'Spotify', 'Airbnb', 'Netflix'],
      steps: [
        {
          id: 's1',
          title: 'Frontend Fundamentals',
          description: 'Learn HTML, CSS, and JavaScript basics',
          estimatedTime: '4 weeks',
          status: 'completed',
          courses: 3,
          skills: ['HTML', 'CSS', 'JavaScript'],
          type: 'course'
        },
        {
          id: 's2',
          title: 'React Development',
          description: 'Master React.js for building user interfaces',
          estimatedTime: '6 weeks',
          status: 'completed',
          courses: 4,
          skills: ['React', 'JSX', 'State Management'],
          type: 'course'
        },
        {
          id: 's3',
          title: 'Backend with Node.js',
          description: 'Learn server-side development with Node.js',
          estimatedTime: '5 weeks',
          status: 'current',
          courses: 3,
          skills: ['Node.js', 'Express', 'APIs'],
          type: 'course'
        },
        {
          id: 's4',
          title: 'Database Design',
          description: 'Work with databases and data modeling',
          estimatedTime: '3 weeks',
          status: 'available',
          courses: 2,
          skills: ['MongoDB', 'SQL', 'Database Design'],
          type: 'course'
        }
      ]
    },
    {
      id: '2',
      title: 'Data Scientist',
      description: 'Extract insights from data using statistical analysis and machine learning',
      category: 'data',
      level: 'advanced',
      duration: '10-15 months',
      totalSteps: 15,
      completedSteps: 2,
      estimatedSalary: { min: 85000, max: 150000 },
      jobOpenings: 8920,
      growthRate: 22.1,
      skills: ['Python', 'R', 'Machine Learning', 'Statistics', 'SQL', 'Tableau'],
      prerequisites: ['Statistics', 'Programming basics', 'Mathematics'],
      outcomes: [
        'Analyze complex datasets',
        'Build predictive models',
        'Create data visualizations',
        'Drive business decisions with data'
      ],
      companies: ['Google', 'Facebook', 'Amazon', 'Netflix', 'Uber'],
      steps: [
        {
          id: 's1',
          title: 'Python for Data Science',
          description: 'Learn Python programming for data analysis',
          estimatedTime: '6 weeks',
          status: 'completed',
          courses: 4,
          skills: ['Python', 'Pandas', 'NumPy'],
          type: 'course'
        },
        {
          id: 's2',
          title: 'Statistics & Probability',
          description: 'Master statistical concepts for data analysis',
          estimatedTime: '5 weeks',
          status: 'current',
          courses: 3,
          skills: ['Statistics', 'Probability', 'Hypothesis Testing'],
          type: 'course'
        }
      ]
    },
    {
      id: '3',
      title: 'UX/UI Designer',
      description: 'Design user-friendly interfaces and experiences for digital products',
      category: 'design',
      level: 'beginner',
      duration: '6-9 months',
      totalSteps: 10,
      completedSteps: 0,
      estimatedSalary: { min: 60000, max: 100000 },
      jobOpenings: 12350,
      growthRate: 8.5,
      skills: ['Figma', 'Adobe XD', 'User Research', 'Prototyping', 'Design Systems'],
      prerequisites: ['Basic design principles', 'Creativity'],
      outcomes: [
        'Design user interfaces and experiences',
        'Conduct user research',
        'Create prototypes and wireframes',
        'Work with development teams'
      ],
      companies: ['Apple', 'Google', 'Adobe', 'Spotify', 'Airbnb'],
      steps: [
        {
          id: 's1',
          title: 'Design Fundamentals',
          description: 'Learn basic design principles and theory',
          estimatedTime: '3 weeks',
          status: 'available',
          courses: 2,
          skills: ['Design Principles', 'Color Theory', 'Typography'],
          type: 'course'
        }
      ]
    }
  ];

  const jobOpportunities: JobOpportunity[] = [
    {
      id: '1',
      title: 'Senior Full Stack Developer',
      company: 'TechCorp Inc.',
      location: 'San Francisco, CA',
      type: 'full-time',
      salary: { min: 95000, max: 130000 },
      requiredSkills: ['React', 'Node.js', 'AWS', 'MongoDB'],
      matchPercentage: 92,
      postedDate: '2024-01-28',
      applicants: 45
    },
    {
      id: '2',
      title: 'Data Scientist',
      company: 'DataFlow Solutions',
      location: 'Remote',
      type: 'remote',
      salary: { min: 85000, max: 115000 },
      requiredSkills: ['Python', 'Machine Learning', 'SQL', 'Statistics'],
      matchPercentage: 78,
      postedDate: '2024-01-29',
      applicants: 67
    },
    {
      id: '3',
      title: 'UX Designer',
      company: 'Design Studio Pro',
      location: 'New York, NY',
      type: 'full-time',
      salary: { min: 70000, max: 90000 },
      requiredSkills: ['Figma', 'User Research', 'Prototyping'],
      matchPercentage: 85,
      postedDate: '2024-01-30',
      applicants: 32
    }
  ];

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'from-green-500 to-emerald-600';
      case 'intermediate': return 'from-yellow-500 to-orange-600';
      case 'advanced': return 'from-red-500 to-pink-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'current': return 'bg-blue-100 text-blue-700';
      case 'available': return 'bg-gray-100 text-gray-700';
      case 'locked': return 'bg-gray-50 text-gray-400';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatSalary = (min: number, max: number) => {
    return `$${(min / 1000).toFixed(0)}K - $${(max / 1000).toFixed(0)}K`;
  };

  const filteredPaths = careerPaths.filter(path => {
    const matchesCategory = selectedCategory === 'all' || path.category === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || path.level === selectedLevel;
    const matchesSearch = path.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         path.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         path.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesLevel && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Target className="w-7 h-7" />
              Career Paths
            </h1>
            <p className="text-purple-100">
              Discover structured learning paths to advance your career
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setViewMode('paths')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'paths'
                  ? 'bg-white text-purple-600'
                  : 'bg-white/20 backdrop-blur-sm hover:bg-white/30'
              }`}
            >
              Career Paths
            </button>
            <button
              onClick={() => setViewMode('jobs')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'jobs'
                  ? 'bg-white text-purple-600'
                  : 'bg-white/20 backdrop-blur-sm hover:bg-white/30'
              }`}
            >
              Job Opportunities
            </button>
          </div>
        </div>

        {/* Career Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Target className="w-6 h-6 mx-auto mb-2 text-purple-200" />
            <p className="text-2xl font-bold">24</p>
            <p className="text-xs text-purple-100">Career Paths</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Briefcase className="w-6 h-6 mx-auto mb-2 text-purple-200" />
            <p className="text-2xl font-bold">15.2K</p>
            <p className="text-xs text-purple-100">Job Openings</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-purple-200" />
            <p className="text-2xl font-bold">18.5%</p>
            <p className="text-xs text-purple-100">Avg Growth Rate</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <DollarSign className="w-6 h-6 mx-auto mb-2 text-purple-200" />
            <p className="text-2xl font-bold">$85K</p>
            <p className="text-xs text-purple-100">Avg Salary</p>
          </div>
        </div>
      </div>

      {viewMode === 'paths' ? (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search career paths, skills, or roles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple-500"
                />
              </div>
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple-500"
              >
                <option value="all">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            {/* Categories */}
            <div className="mt-4">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                      selectedCategory === category.id
                        ? 'border-brand-purple-500 bg-brand-purple-50 text-brand-purple-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {category.icon}
                    <span className="text-sm">{category.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Career Paths */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredPaths.map((path) => (
              <div key={path.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all">
                <div className={`h-2 bg-gradient-to-r ${getLevelColor(path.level)}`}></div>
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{path.title}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${
                          path.level === 'beginner' ? 'bg-green-100 text-green-700' :
                          path.level === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {path.level}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-4">{path.description}</p>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-medium">{path.completedSteps}/{path.totalSteps} steps</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-brand-purple-600 h-2 rounded-full transition-all"
                        style={{ width: `${(path.completedSteps / path.totalSteps) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Key Info */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <Clock className="w-4 h-4 text-gray-600 mx-auto mb-1" />
                      <p className="text-xs text-gray-600">Duration</p>
                      <p className="text-sm font-medium">{path.duration}</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <DollarSign className="w-4 h-4 text-gray-600 mx-auto mb-1" />
                      <p className="text-xs text-gray-600">Salary</p>
                      <p className="text-sm font-medium">{formatSalary(path.estimatedSalary.min, path.estimatedSalary.max)}</p>
                    </div>
                  </div>

                  {/* Market Info */}
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      <span>{path.jobOpenings.toLocaleString()} jobs</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span className="text-green-600">+{path.growthRate}%</span>
                    </div>
                  </div>

                  {/* Skills */}
                  <div className="mb-6">
                    <p className="text-sm font-medium text-gray-900 mb-2">Key Skills</p>
                    <div className="flex flex-wrap gap-1">
                      {path.skills.slice(0, 4).map((skill) => (
                        <span
                          key={skill}
                          className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded"
                        >
                          {skill}
                        </span>
                      ))}
                      {path.skills.length > 4 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                          +{path.skills.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setSelectedPath(path.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700 font-medium"
                    >
                      <Play className="w-4 h-4" />
                      Start Path
                    </button>
                    <button className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg">
                      <BookmarkPlus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        /* Job Opportunities View */
        <div className="space-y-4">
          {jobOpportunities.map((job) => (
            <div key={job.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      job.matchPercentage >= 90 ? 'bg-green-100 text-green-700' :
                      job.matchPercentage >= 75 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {job.matchPercentage}% match
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-1">
                      <Building className="w-4 h-4" />
                      <span>{job.company}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{job.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(job.postedDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-gray-900">
                    {formatSalary(job.salary.min, job.salary.max)}
                  </p>
                  <p className="text-sm text-gray-600">{job.applicants} applicants</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {job.requiredSkills.map((skill) => (
                  <span
                    key={skill}
                    className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded"
                  >
                    {skill}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${
                  job.type === 'remote' ? 'bg-green-100 text-green-700' :
                  job.type === 'full-time' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {job.type.replace('-', ' ')}
                </span>
                <div className="flex gap-2">
                  <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">
                    Save
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700 font-medium">
                    Apply Now
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Path Detail Modal */}
      {selectedPath && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {(() => {
              const path = careerPaths.find(p => p.id === selectedPath);
              if (!path) return null;

              return (
                <>
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{path.title}</h3>
                        <p className="text-gray-600">{path.description}</p>
                      </div>
                      <button
                        onClick={() => setSelectedPath(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    {/* Path Steps */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Learning Path</h4>
                      <div className="space-y-4">
                        {path.steps.map((step, index) => (
                          <div key={step.id} className="flex items-start gap-4">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                              step.status === 'completed' ? 'bg-green-100 text-green-700' :
                              step.status === 'current' ? 'bg-blue-100 text-blue-700' :
                              step.status === 'available' ? 'bg-gray-100 text-gray-700' :
                              'bg-gray-50 text-gray-400'
                            }`}>
                              {step.status === 'completed' ? <CheckCircle className="w-4 h-4" /> :
                               step.status === 'locked' ? <Lock className="w-4 h-4" /> :
                               index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-1">
                                <h5 className="font-medium text-gray-900">{step.title}</h5>
                                <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(step.status)}`}>
                                  {step.status}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span>{step.estimatedTime}</span>
                                <span>{step.courses} courses</span>
                                <div className="flex flex-wrap gap-1">
                                  {step.skills.slice(0, 3).map(skill => (
                                    <span key={skill} className="px-1 py-0.5 bg-gray-100 rounded">
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Path Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">Learning Outcomes</h5>
                        <ul className="space-y-1 text-sm text-gray-600">
                          {path.outcomes.map((outcome, index) => (
                            <li key={index} className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              {outcome}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">Top Companies Hiring</h5>
                        <div className="flex flex-wrap gap-2">
                          {path.companies.map((company) => (
                            <span key={company} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded">
                              {company}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setSelectedPath(null)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      >
                        Close
                      </button>
                      <button className="px-6 py-2 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700">
                        Start Learning Path
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}